import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Card,
    Divider,
    Grid,
    Snackbar,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import AdditionalWorkshopCartContext from 'contexts/AdditionalWorkshopCartContext';
import useBoolean from 'hooks/useBoolean';
import ChevronLeft from 'icons/ChevronLeft';
import ShoppingCart from 'icons/ShoppingCart';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import formatCurrency from 'utils/formatCurrency';

const AdditionalPayment = () => {
    const cart = useContext(AdditionalWorkshopCartContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const openSnackBar = useBoolean();
    const isPaymentProcess = useBoolean();
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [additionalPackage, setAdditionalPackage] = useState(null);

    const unpaid = useMemo(
        () => cart.workshops.filter((w) => !w.isPaid).map((w) => w.workshop),
        [cart.workshops],
    );

    useEffect(() => {
        const init = async () => {
            try {
                const resInv = await axiosInstance.get('/api/prv/member-invoices', {
                    params: { packageType: 'additional' },
                });
                const pending = (resInv?.data || []).find(
                    (item) => item?.status?.value === 'pending',
                );
                if (pending) {
                    navigate('/dashboard/payment-complete-additional');
                    return;
                }
            } catch (_) {}
            if (unpaid.length === 0) {
                navigate('/dashboard/choose-additional-workshop');
                return;
            }
            try {
                const res = await axiosInstance.get('/api/prv/packages', {
                    params: { type: 'additional' },
                });
                setAdditionalPackage(res?.data?.[0] || null);
            } catch (err) {
                setSnackbarMessage('Failed to load additional package');
                openSnackBar.onTrue();
            }
        };
        
        init();
    }, [unpaid.length]);

    const handleCreateInvoice = async () => {
        if (!additionalPackage) {
            setSnackbarMessage('Additional package not available');
            openSnackBar.onTrue();
            return;
        }

        isPaymentProcess.onTrue();
        const values = {
            package_id: additionalPackage.id,
            workshops: unpaid.map((w) => w.id),
        };

        await axiosInstance
            .post('/api/prv/member-invoices', values)
            .then(() => {
                navigate('/dashboard/payment-complete-additional');
            })
            .catch((err) => {
                setSnackbarMessage(err.message || 'Error');
                openSnackBar.onTrue();
            })
            .finally(() => isPaymentProcess.onFalse());
    };

    const subtotal = unpaid.reduce((acc, cur) => acc + parseInt(cur.price || 0), 0);

    return (
        <>
            <Snackbar
                open={openSnackBar.value}
                autoHideDuration={3000}
                onClose={() => openSnackBar.onFalse()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={'error'}
                    onClose={() => openSnackBar.onFalse()}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <DashboardLayout>
                <Box pt={2} pb={4}>
                    <Box mt={2}>
                        <Button
                            disableRipple
                            startIcon={<ChevronLeft />}
                            variant="contained"
                            size="small"
                            onClick={() => navigate('/dashboard/choose-additional-workshop')}
                        >
                            Back
                        </Button>
                    </Box>
                    <Grid container spacing={3} mt={3}>
                        <Grid item md={7} xs={12}>
                            <Card sx={{ p: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="h4" fontWeight={800} mb={2}>
                                        Your Order Summary
                                    </Typography>
                                    <img
                                        src={process.env.PUBLIC_URL + '/logo-semarang.webp'}
                                        alt="logo"
                                        style={{ maxWidth: '200px', height: 'auto' }}
                                    />
                                </Box>

                                <Typography variant="h6" color="text.main" sx={{ mt: 2 }}>
                                    Selected Workshops
                                </Typography>

                                <Stack spacing={1}>
                                    {unpaid.map((item, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.date} • {item.time}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" fontWeight={700}>
                                                {formatCurrency(parseInt(item.price || 0))}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>

                                <Divider sx={{ my: 2 }} />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Subtotal
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        color="error.main"
                                        fontWeight={800}
                                    >
                                        {formatCurrency(subtotal)}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item md={5} xs={12}>
                            <Card sx={{ p: 3 }}>
                                <Typography variant="h4" fontWeight={800} mb={2}>
                                    Proceed to Payment
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Stack spacing={1}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography variant="body1" color="text.main">
                                            Final Amount
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight={600}
                                            color={theme.palette.error.main}
                                        >
                                            {formatCurrency(subtotal)}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.error.main,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                            letterSpacing={0.5}
                                        >
                                            Your Final Amount: {formatCurrency(subtotal)}
                                        </Typography>
                                    </Box>
                                    <LoadingButton
                                        variant="contained"
                                        disabled={isPaymentProcess.value}
                                        startIcon={<ShoppingCart />}
                                        onClick={() => handleCreateInvoice()}
                                        loading={isPaymentProcess.value}
                                    >
                                        {isPaymentProcess.value ? 'Processing...' : 'PAY NOW'}
                                    </LoadingButton>
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </DashboardLayout>
        </>
    );
};

export default AdditionalPayment;
